import { BackofficeApiError } from '../../../lib/api/backoffice/http'

export type DashboardModuleKey =
  | 'dashboard_support'
  | 'dashboard_status_summary'
  | 'dashboard_status_upgrades'
  | 'dashboard_readiness'
  | 'release_notes'

const unavailableDashboardModules = new Set<DashboardModuleKey>()

export const isDashboardModuleUnavailable = (moduleKey: DashboardModuleKey): boolean =>
  unavailableDashboardModules.has(moduleKey)

export const markDashboardModuleUnavailable = (moduleKey: DashboardModuleKey): void => {
  unavailableDashboardModules.add(moduleKey)
}

export const markDashboardModuleAvailable = (moduleKey: DashboardModuleKey): void => {
  unavailableDashboardModules.delete(moduleKey)
}

export const resetDashboardModuleAvailability = (): void => {
  unavailableDashboardModules.clear()
}

export const isModuleUnavailableError = (error: unknown): error is BackofficeApiError =>
  error instanceof BackofficeApiError && error.status === 404

export const runDashboardOptionalRequest = async <Payload>({
  moduleKey,
  load,
  fallbackValue,
}: {
  moduleKey: DashboardModuleKey
  load: () => Promise<Payload>
  fallbackValue: Payload
}): Promise<{ value: Payload; available: boolean }> => {
  if (isDashboardModuleUnavailable(moduleKey)) {
    return {
      value: fallbackValue,
      available: false,
    }
  }

  try {
    const value = await load()
    markDashboardModuleAvailable(moduleKey)
    return {
      value,
      available: true,
    }
  } catch (error) {
    if (isModuleUnavailableError(error)) {
      markDashboardModuleUnavailable(moduleKey)
      return {
        value: fallbackValue,
        available: false,
      }
    }
    throw error
  }
}
